import { gql } from "@apollo/client";
import { Spinner } from "@chakra-ui/react";
import { useState } from "react";
import { useAuthContext } from "../../auth";
import { TemplateSettingsForm } from "../../components/TemplateSettingsForm";
import { toaster } from "../../components/ui/toaster";
import {
  type Templates_Insert_Input,
  type Templates_Set_Input,
  TemplatesDocument,
  type TemplatesQuery,
  useCreateTemplateMutation,
  useDeleteTemplatesMutation,
  useSetDefaultTemplateMutation,
  useTemplatesQuery,
  useUpdateTemplateMutation,
} from "../../graphql/generated/apollo";
import { useShopContext } from "../../shop";

gql`
  query Templates($shop_id: uuid!) {
    templates(where: { shop_id: { _eq: $shop_id } }, order_by: [{ name: asc }]) {
      ...TemplateSettingsForm_Template
    }
    categories(where: { shop_id: { _eq: $shop_id } }, order_by: [{ position: asc }, { name: asc }]) {
      ...TemplateSettingsForm_Category
    }
  }

  mutation UpdateTemplate($id: uuid!, $name: String!) {
    update_templates_by_pk(pk_columns: { id: $id }, _set: { name: $name }) {
      id
      name
      is_default
    }
  }

  mutation CreateTemplate($template: templates_insert_input!) {
    insert_templates_one(object: $template) {
      id
      name
      is_default
      category_templates {
        category_id
      }
    }
  }

  mutation DeleteTemplates($ids: [uuid!]!) {
    delete_templates(where: { id: { _in: $ids } }) {
      affected_rows
      returning {
        id
      }
    }
  }

  mutation SetDefaultTemplate($shop_id: uuid!, $id: uuid!) {
    update_templates(where: { shop_id: { _eq: $shop_id } }, _set: { is_default: false }) {
      affected_rows
      returning {
        id
      }
    }
    update_templates_by_pk(pk_columns: { id: $id }, _set: { is_default: true }) {
      id
      is_default
    }
  }
`;

export function Templates() {
  const { user, isLoading: authLoading } = useAuthContext();
  const { currentShopId, isLoading: shopLoading } = useShopContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const { data, loading, error } = useTemplatesQuery({
    variables: {
      shop_id: currentShopId ?? "",
    },
    skip: !user || !currentShopId,
  });

  // 単一テンプレートの作成
  const [createTemplate] = useCreateTemplateMutation({
    onCompleted: () => {
      toaster.create({
        title: "テンプレートを追加しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  // 単一テンプレートの更新
  const [updateTemplate] = useUpdateTemplateMutation({
    onCompleted: () => {
      toaster.create({
        title: "テンプレートを更新しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  // テンプレートの削除
  const [deleteTemplates] = useDeleteTemplatesMutation({
    onCompleted: () => {
      toaster.create({
        title: "テンプレートを削除しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  const [setDefaultTemplate] = useSetDefaultTemplateMutation({
    onCompleted: () => {
      toaster.create({
        title: "デフォルトテンプレートを設定しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  if (authLoading || shopLoading || loading) {
    return <Spinner />;
  }

  if (error) {
    toaster.create({
      title: "エラーが発生しました",
      description: error.message,
      type: "error",
    });
  }

  const handleAddTemplate = async (template: Templates_Insert_Input) => {
    const result = await createTemplate({
      variables: {
        template: { ...template, shop_id: currentShopId ?? "" },
      },
      update(cache, { data }) {
        if (!data?.insert_templates_one) {
          return;
        }

        const existingData = cache.readQuery<TemplatesQuery>({
          query: TemplatesDocument,
          variables: { shop_id: currentShopId ?? "" },
        });

        if (!existingData) return;

        cache.writeQuery({
          query: TemplatesDocument,
          variables: { shop_id: currentShopId ?? "" },
          data: {
            ...existingData,
            templates: [...existingData.templates, data.insert_templates_one],
          },
        });
      },
    });
  };

  const handleUpdateTemplate = (template: Templates_Set_Input) => {
    if (!template.id) return;

    updateTemplate({
      variables: {
        id: template.id,
        name: template.name ?? "",
      },
    });
  };

  const handleDeleteTemplate = (id: string) => {
    deleteTemplates({
      variables: {
        ids: [id],
      },
      update: (cache, { data }) => {
        if (!data?.delete_templates) {
          return;
        }

        cache.evict({
          id: cache.identify({ __typename: "templates", id }),
        });
      },
    });
  };

  const handleSetDefaultTemplate = (id: string) => {
    setDefaultTemplate({
      variables: {
        shop_id: currentShopId ?? "",
        id,
      },
      update: (cache, { data }) => {
        if (!data?.update_templates) {
          return;
        }
        // data.update_templates.returning[0].idのis_defaultをfalseにする
        const existingData = cache.readQuery<TemplatesQuery>({
          query: TemplatesDocument,
          variables: { shop_id: currentShopId ?? "" },
        });

        if (!existingData) return;

        cache.writeQuery({
          query: TemplatesDocument,
          variables: { shop_id: currentShopId ?? "" },
          data: {
            ...existingData,
            templates: existingData.templates.map((template) => ({ ...template, is_default: template.id === id })),
          },
        });
      },
    });
  };

  return (
    <TemplateSettingsForm
      templates={data?.templates || []}
      categories={data?.categories || []}
      selectedCategoryId={selectedCategoryId}
      onCategoryChange={setSelectedCategoryId}
      onAdd={handleAddTemplate}
      onUpdate={handleUpdateTemplate}
      onDelete={handleDeleteTemplate}
      onSetDefault={handleSetDefaultTemplate}
    />
  );
}
